/**
 * Grading Scheme Resolver
 *
 * Single source of truth for "how is this student graded?". Every consumer —
 * grade entry, the report card, section averages, the backend after-save hooks —
 * branches on what this returns instead of hardcoding a quarter count or a
 * grade-level list.
 *
 * Background: DepEd SY 2026-2027 reforms.
 *   DO 009, s. 2026 — four quarters become three terms; the school year now
 *                     starts in June.
 *   DO 015, s. 2026 — Key Stage 1 moves to descriptive (letter) grading, phased
 *                     in one grade level per year; Grades 2-12 stay numeric but
 *                     gain a qualitative descriptor.
 *   DM 012, s. 2026 — Strengthened SHS in Grade 11.
 *
 * School years before 2026-2027 must keep resolving to the legacy four-quarter
 * numeric scheme so historical cards reprint unchanged.
 *
 * NOTE: sis-backend/common/lib/gradingScheme.js is a copy of this file. Keep the
 * two in sync; both repos assert against __fixtures__/gradingScheme.vectors.json.
 * Written in CommonJS so one body serves both.
 */

// SY 2026-2027 is the first three-term year.
var FIRST_REFORM_YEAR = 2026;

// Key Stage 1 ends at Grade 3.
//
// Descriptive grading used to phase in one level per year across this range -
// 2026-2027 Grade 1, then Grade 2, then Grade 3. The school reports a number
// and a letter to parents instead, and a letter cannot be turned back into a
// number, so all three sit on numbers from the start and print both. The range
// still matters: it is exactly the levels whose card carries the letter.
var PHASE_IN_MAX = 3;

// Grades 1-3, per the PACE form's General Instructions.
var PACE_SCALE = [
  { key: "A", value: "A", text: "A - Advancing" },
  { key: "B", value: "B", text: "B - Benchmarking" },
  { key: "C", value: "C", text: "C - Connecting" },
  { key: "D", value: "D", text: "D - Developing" },
  { key: "E", value: "E", text: "E - Emerging" },
];

// Kindergarten uses a different, three-level scale.
var KINDER_SCALE = [
  { key: "CO", value: "CO", text: "CO - Consistent" },
  { key: "DV", value: "DV", text: "DV - Developing" },
  { key: "BG", value: "BG", text: "BG - Beginning" },
];

// Qualitative descriptor shown beside a NUMERIC grade, Grades 2-12.
//
// The letter is the same A-E the PACE scale uses, and it is carried here rather
// than taken from the label's first character: the two happen to agree today,
// and a reworded label should not silently change a learner's mark.
var NUMERIC_DESCRIPTORS = [
  {
    min: 90, max: 100, letter: "A",
    label: "Advancing", filipino: "Namumukod Tangi",
    description: "Consistently demonstrates advanced skills, understanding, " +
      "and values beyond expectations; performs with confidence, accuracy, " +
      "and independence",
  },
  {
    min: 80, max: 89, letter: "B",
    label: "Benchmarking", filipino: "Napamamalas",
    description: "Demonstrates expected skills, understanding, and values at " +
      "grade level with consistency; performs tasks accurately and independently",
  },
  {
    min: 75, max: 79, letter: "C",
    label: "Connecting", filipino: "Natutungo",
    description: "Demonstrates foundational skills, understanding, and values; " +
      "applies learning in familiar tasks with minimal guidance",
  },
  {
    min: 65, max: 74, letter: "D",
    label: "Developing", filipino: "Napauunlad",
    description: "Demonstrates partial understanding and inconsistent " +
      "application of skills and values; requires targeted support and regular " +
      "practice to improve performance",
  },
  {
    min: 0, max: 64, letter: "E",
    label: "Emerging", filipino: "Nagsisimula",
    description: "Beginning to demonstrate basic skills, understanding, and " +
      "values; requires intensive support and close guidance",
  },
];

var QUARTER_PERIODS = [
  { index: 1, label: "1st Quarter", shortLabel: "1st", dbField: "firstQuarter",
    letterField: null, conductField: "firstQuarterFinal", remarkField: "firstQuarter" },
  { index: 2, label: "2nd Quarter", shortLabel: "2nd", dbField: "secondQuarter",
    letterField: null, conductField: "secondQuarterFinal", remarkField: "secondQuarter" },
  { index: 3, label: "3rd Quarter", shortLabel: "3rd", dbField: "thirdQuarter",
    letterField: null, conductField: "thirdQuarterFinal", remarkField: "thirdQuarter" },
  { index: 4, label: "4th Quarter", shortLabel: "4th", dbField: "fourthQuarter",
    letterField: null, conductField: "fourthQuarterFinal", remarkField: "fourthQuarter" },
];

// Terms 1-3 reuse the first three quarter columns. No data migration: a term
// grade lands in exactly the column the equivalent quarter grade used to.
// Descriptive grades go to the parallel *Letter string fields instead, so no
// legacy numeric code path can ever see a letter.
var TERM_PERIODS = [
  { index: 1, label: "Term 1", shortLabel: "T1", dbField: "firstQuarter",
    letterField: "firstQuarterLetter", conductField: "firstQuarterFinal", remarkField: "firstQuarter" },
  { index: 2, label: "Term 2", shortLabel: "T2", dbField: "secondQuarter",
    letterField: "secondQuarterLetter", conductField: "secondQuarterFinal", remarkField: "secondQuarter" },
  { index: 3, label: "Term 3", shortLabel: "T3", dbField: "thirdQuarter",
    letterField: "thirdQuarterLetter", conductField: "thirdQuarterFinal", remarkField: "thirdQuarter" },
];

// Escape hatch. If DepEd shifts the phase-in mid-year, add an entry here rather
// than editing the logic. Keyed "startYear|gradeKey".
var OVERRIDES = {
  // "2027|Grade 3": { mode: "descriptive-pace", scale: "pace-5" },
};

// A school year identifier is a bare year, a "YYYY-YYYY" range, or that range
// with an "SY" prefix. Anything else - notably a uuid - is not one.
function looksLikeSchoolYear(value) {
  return /^\s*(SY\s*)?\d{4}\s*(-\s*\d{4})?\s*$/i.test(value);
}

// Guards against a parse that produced a number-shaped nonsense year.
function plausibleYear(year) {
  if (typeof year !== "number" || isNaN(year)) return null;
  return year >= 1900 && year <= 2200 ? year : null;
}

/**
 * Accepts a start year, a "2026-2027" / "SY 2026-2027" string, or a SchoolYear
 * document. Returns the start year as a number, or null if undeterminable.
 *
 * Returns null rather than guessing when the input is not recognisably a school
 * year, so a stray id can never silently select a grading mode.
 */
function parseSchoolYearStart(schoolYear) {
  if (schoolYear === null || schoolYear === undefined) return null;
  if (typeof schoolYear === "number") return plausibleYear(schoolYear);

  if (typeof schoolYear === "string") {
    // A uuid contains four hex digits often enough to look like a year:
    // "3f2504e0-..." would parse as 2504 and silently pick a grading mode.
    // Reject anything that is not recognisably a school year.
    if (!looksLikeSchoolYear(schoolYear)) return null;
    var m = schoolYear.match(/(\d{4})/);
    return m ? plausibleYear(parseInt(m[1], 10)) : null;
  }

  if (schoolYear.name) {
    var byName = String(schoolYear.name).match(/(\d{4})/);
    if (byName) return plausibleYear(parseInt(byName[1], 10));
  }

  if (schoolYear.startDate) {
    var d = new Date(schoolYear.startDate);
    if (!isNaN(d.getTime())) {
      // A June-start school year is named for its start year; a Jan-May
      // startDate means the record belongs to the year that began last June.
      return plausibleYear(d.getMonth() + 1 >= 6 ? d.getFullYear() : d.getFullYear() - 1);
    }
  }

  return null;
}

/**
 * Strips the strand suffix so "Grade 12 - HUMMS" resolves like "Grade 12".
 * The HUMMS misspelling is deliberate and load-bearing: live Section documents
 * contain that literal string. Do not correct it here.
 */
function normalizeGradeLevel(gradeLevel) {
  if (!gradeLevel) return { key: "", num: null };

  var s = String(gradeLevel).trim();

  // Preschool is graded like Kinder - CO/DV/BG, never a number - so Nursery and
  // Pre-Kinder resolve to the same period 0. They keep their own names: the
  // card heading and the subject list still need to tell them apart.
  if (/nursery/i.test(s)) return { key: "Nursery", num: 0 };
  if (/pre[\s-]*kinder/i.test(s)) return { key: "Pre-Kinder", num: 0 };

  if (/kinder/i.test(s)) {
    return { key: "Kinder", num: 0 };
  }

  var m = s.match(/Grade\s*(\d{1,2})/i);
  if (!m) return { key: s, num: null };

  return { key: "Grade " + m[1], num: parseInt(m[1], 10) };
}

/**
 * The whole descriptor band a mark falls in - letter, name, Filipino name and
 * the school's written description of what it means.
 *
 * The card prints the description against a term's general average, so a
 * parent reads what the number says about their child rather than only the
 * number. Returns null for an ungraded mark: zero means "not marked yet"
 * everywhere else on the card and must not read as Emerging.
 */
function bandFor(value) {
  var v = Number(value);
  if (!isFinite(v) || v === 0) return null;

  for (var i = 0; i < NUMERIC_DESCRIPTORS.length; i++) {
    var band = NUMERIC_DESCRIPTORS[i];
    if (v >= band.min && v <= band.max) return band;
  }
  return null;
}

/**
 * The A-E letter for a numeric grade, for the Grades 1-3 card that prints both.
 *
 * Computed, never stored. A learner has one mark - the number - and the letter
 * is a reading of it, so the two cannot drift apart the way two stored fields
 * would. 90 is an A, 85 a B, on the same bands the descriptor uses.
 */
function letterFor(value) {
  var v = Number(value);
  if (!isFinite(v) || v === 0) return "";

  for (var i = 0; i < NUMERIC_DESCRIPTORS.length; i++) {
    var band = NUMERIC_DESCRIPTORS[i];
    if (v >= band.min && v <= band.max) return band.letter;
  }
  return "";
}

/** Maps a numeric grade to its DO 015 qualitative descriptor. */
function descriptorFor(value) {
  var v = Number(value);
  if (!isFinite(v) || v === 0) return "";

  for (var i = 0; i < NUMERIC_DESCRIPTORS.length; i++) {
    var band = NUMERIC_DESCRIPTORS[i];
    if (v >= band.min && v <= band.max) return band.label;
  }
  return "";
}

/**
 * Groups terms into semesters for the SHS two-table card. Reads
 * SchoolYear.terms[].semester when present so the registrar controls the split;
 * otherwise falls back to a sane default per period count.
 */
function buildSemesters(terms, periodCount) {
  if (Array.isArray(terms) && terms.length &&
      terms.some(function (t) { return t.semester !== null && t.semester !== undefined; })) {
    var bySem = {};
    terms.forEach(function (t) {
      var s = t.semester || 1;
      if (!bySem[s]) bySem[s] = [];
      bySem[s].push(t.index);
    });
    return Object.keys(bySem).sort().map(function (s) {
      return { index: Number(s), terms: bySem[s].sort() };
    });
  }

  if (periodCount === 4) {
    return [{ index: 1, terms: [1, 2] }, { index: 2, terms: [3, 4] }];
  }
  // Three-term default: Grade 12's second-semester subjects don't start until
  // January anyway. Confirm with the registrar; override via SchoolYear.terms.
  return [{ index: 1, terms: [1, 2] }, { index: 2, terms: [3] }];
}

/**
 * @param {number|string|object} schoolYear start year, "YYYY-YYYY", or a SchoolYear doc
 * @param {string} gradeLevel               "Grade 1", "Grade 12 - HUMMS", "Kinder"
 * @param {object} [opts]                   { levelType }
 */
function resolveGradingScheme(schoolYear, gradeLevel, opts) {
  var options = opts || {};
  var startYear = parseSchoolYearStart(schoolYear);
  var gl = normalizeGradeLevel(gradeLevel);
  var syDoc = (schoolYear && typeof schoolYear === "object") ? schoolYear : null;
  var levelType = options.levelType || "";

  var mode;
  var scale;
  var override = OVERRIDES[startYear + "|" + gl.key];

  if (override) {
    mode = override.mode;
    scale = override.scale || (override.mode === "descriptive-pace" ? "pace-5" : "numeric");
  } else if (startYear === null || startYear < FIRST_REFORM_YEAR) {
    // Historical years must render exactly as they always have.
    mode = "numeric-4q";
    scale = "numeric";
  } else if (gl.num === 0) {
    mode = "descriptive-pace";
    scale = "kinder-3";
  } else {
    // Key Stage 1 was letters only. The school reports both to parents, and a
    // letter cannot be turned back into a number - A is anything from 90 to
    // 100 - so the number is what gets entered and the letter is read off it.
    // Grades 1 to 3 therefore all sit on numbers, and their card prints
    // "90 (A)". Kinder above keeps CO/DV/BG: it has no numeric equivalent.
    mode = "numeric-3term";
    scale = "numeric";
  }

  var isLegacy = mode === "numeric-4q";
  var periods = isLegacy ? QUARTER_PERIODS : TERM_PERIODS;
  var periodCount = periods.length;

  // Grade 12 keeps the 2016 strand curriculum and its two-table semester card.
  // Grade 11 under the Strengthened curriculum carries SHSACAD/SHSTECHPRO, which
  // deliberately does NOT match here, so it falls through to the whole-year path.
  var isLegacySHS = /^SHS(STEM|HUMSS|ABM)$/.test(levelType);

  var cardVariant;
  if (isLegacy) cardVariant = "legacy-4q";
  else if (mode === "descriptive-pace") cardVariant = "descriptive";
  else if (isLegacySHS) cardVariant = "shs-2table";
  else cardVariant = "numeric-3term";

  var isNumeric = scale === "numeric";

  // Grade 1 only, from the first reform year. Kinder has no PACE form.
  var usesPaceForm = gl.num === 1 && startYear !== null &&
    startYear >= FIRST_REFORM_YEAR;

  return {
    schoolYearStart: startYear,
    schoolYearName: syDoc ? syDoc.name : null,
    gradeLevel: gl.key,
    gradeNumber: gl.num,
    levelType: levelType,

    mode: mode,
    scale: scale,
    // PACE keeps its A-E options even though Grade 1's subject marks are
    // numbers now: the competency form is a separate assessment, rated on that
    // scale, and it must not follow the subject grades onto numbers.
    scaleOptions: usesPaceForm ? PACE_SCALE
      : scale === "kinder-3" ? KINDER_SCALE
        : [],

    periods: periods,
    periodCount: periodCount,
    periodNoun: isLegacy ? "Quarter" : "Term",

    // DO 015 transmuted grades floor at 60; the legacy floor stays 70 so old
    // school years keep rejecting what they always rejected.
    minGrade: isLegacy ? 70 : 60,
    maxGrade: 100,
    passingGrade: 75,

    descriptorFor: isNumeric ? descriptorFor : function () { return ""; },
    showDescriptorColumn: isNumeric && !isLegacy,

    // A general average over letters is undefined, and DepEd bars Key Stage 1
    // honors outright, so the descriptive card omits the AVERAGE row entirely.
    // Every numeric level carries a general average, Grade 1 included. It was
    // held back when Grade 1 was graded in letters - an average over letters is
    // undefined, and DO 015 confers no Key Stage 1 honors - but the school
    // reports a number there now and wants the average with it.
    usesGeneralAverage: isNumeric,

    // Tied to the grade level and the reform year, not to the grading mode.
    // It used to require descriptive-pace, so the moment Grade 1's subject
    // marks became numbers the PACE form would have vanished with them.
    usesPace: usesPaceForm,

    // Grades 1 to 3 print the letter beside the number; nobody else does.
    showLetterColumn: isNumeric && !isLegacy && gl.num !== null &&
      gl.num >= 1 && gl.num <= PHASE_IN_MAX,
    letterFor: isNumeric ? letterFor : function () { return ""; },

    // The band a mark falls in, for the written description the card prints
    // against a term's general average.
    bandFor: isNumeric ? bandFor : function () { return null; },

    cardVariant: cardVariant,
    semesters: cardVariant === "shs-2table"
      ? buildSemesters(syDoc && syDoc.terms, periodCount)
      : null,

    /** Field on a Grade document holding this period's value. */
    fieldFor: function (periodIndex) {
      var p = null;
      for (var i = 0; i < periods.length; i++) {
        if (periods[i].index === Number(periodIndex)) { p = periods[i]; break; }
      }
      if (!p) return null;
      return mode === "descriptive-pace" ? p.letterField : p.dbField;
    },

    isValidPeriod: function (periodIndex) {
      var n = Number(periodIndex);
      return n >= 1 && n <= periodCount && n === Math.floor(n);
    },

    /**
     * round((T1+T2+T3)/3), or /4 for legacy years. Returns "" while any period
     * is still ungraded — the card shows blanks until the year completes.
     * Letters have no arithmetic; the latest recorded descriptor stands.
     */
    finalGradeOf: function (grade) {
      if (!grade) return "";

      if (mode === "descriptive-pace") {
        for (var i = periods.length - 1; i >= 0; i--) {
          var letter = grade[periods[i].letterField];
          if (letter) return letter;
        }
        return "";
      }

      var values = periods.map(function (p) { return Number(grade[p.dbField]) || 0; });
      var incomplete = values.some(function (v) { return v === 0; });
      if (incomplete) return "";

      var total = values.reduce(function (a, b) { return a + b; }, 0);
      return Math.round(total / values.length);
    },
  };
}

/**
 * The grade column a period writes to, without needing a resolved scheme.
 *
 * Terms 1-3 reuse the first three quarter columns, so one table serves both
 * calendars; passing the period count is what stops a four-quarter default from
 * handing back "fourthQuarter" on a three-term year, which is a column the
 * migration deliberately left dormant.
 */
function periodDbField(periodIndex, periodCount) {
  var periods = Number(periodCount) === 3 ? TERM_PERIODS : QUARTER_PERIODS;
  var n = Number(periodIndex);
  for (var i = 0; i < periods.length; i++) {
    if (periods[i].index === n) return periods[i].dbField;
  }
  return null;
}

exports.resolveGradingScheme = resolveGradingScheme;
exports.periodDbField = periodDbField;
exports.parseSchoolYearStart = parseSchoolYearStart;
exports.looksLikeSchoolYear = looksLikeSchoolYear;
exports.normalizeGradeLevel = normalizeGradeLevel;
exports.descriptorFor = descriptorFor;
exports.letterFor = letterFor;
exports.bandFor = bandFor;
exports.buildSemesters = buildSemesters;
exports.PACE_SCALE = PACE_SCALE;
exports.KINDER_SCALE = KINDER_SCALE;
exports.NUMERIC_DESCRIPTORS = NUMERIC_DESCRIPTORS;
exports.QUARTER_PERIODS = QUARTER_PERIODS;
exports.TERM_PERIODS = TERM_PERIODS;
exports.FIRST_REFORM_YEAR = FIRST_REFORM_YEAR;
